/**
 * Assign-crew picker for a PICKUP (workstream C) — the pickup twin of
 * AssignProjectCrewButton. Same roster + multi-select + optional lead star, but:
 *   - a pickup doesn't REQUIRE a lead (it's a warehouse errand), and
 *   - double-booking is WARN-not-block (D-11): the save always succeeds; the
 *     response's `warnings` (overlaps to eyeball) surface as a heads-up toast,
 *     there is no reject / "reassign & save" retry.
 */
import { useMemo, useState } from 'react';
import { Loader2, Star, TriangleAlert, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useDispatchDay } from '@/api/dispatch';
import { usePickupAssignments, useSetPickupAssignments } from '@/api/pickups';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

export interface AssignPickupCrewButtonProps {
  pickup: { id: string; title: string; scheduled_start: string | null };
  variant?: 'button' | 'link';
}

function todayLocal(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
/** The pickup's scheduled local date (for the roster load), else today. */
function loadDayFor(scheduledStart: string | null): string {
  if (!scheduledStart) return todayLocal();
  const d = new Date(scheduledStart);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function AssignPickupCrewButton({ pickup, variant = 'button' }: AssignPickupCrewButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {variant === 'button' ? (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <Users className="w-3.5 h-3.5" />
          Assign crew
        </Button>
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          className="inline-flex items-center gap-1 text-[11px] text-ink-faint hover:text-accent shrink-0"
        >
          <Users className="w-3.5 h-3.5" />
          Assign crew
        </button>
      )}
      {open && <AssignPickupCrewDialog pickup={pickup} onClose={() => setOpen(false)} />}
    </>
  );
}

function AssignPickupCrewDialog({
  pickup,
  onClose,
}: {
  pickup: AssignPickupCrewButtonProps['pickup'];
  onClose: () => void;
}) {
  const loadDay = loadDayFor(pickup.scheduled_start);
  const dispatch = useDispatchDay(loadDay);
  const current = usePickupAssignments(pickup.id);
  const set = useSetPickupAssignments(pickup.id);
  const rows = dispatch.data?.crew_rows ?? [];

  const loaded = useMemo(() => {
    const sel = new Set<string>();
    let lead: string | null = null;
    for (const a of current.data?.assignments ?? []) {
      sel.add(a.crew_member_id);
      if (a.is_lead) lead = a.crew_member_id;
    }
    return { sel, lead };
  }, [current.data]);

  const [override, setOverride] = useState<{ sel: Set<string>; lead: string | null } | null>(null);
  const view = override ?? loaded;

  function toggle(cid: string) {
    const base = override ?? loaded;
    const sel = new Set(base.sel);
    let lead = base.lead;
    if (sel.has(cid)) {
      sel.delete(cid);
      if (lead === cid) lead = null;
    } else {
      sel.add(cid);
    }
    setOverride({ sel, lead });
  }

  function makeLead(cid: string) {
    const base = override ?? loaded;
    if (!base.sel.has(cid)) return;
    // Toggle: clicking the current lead clears it (a pickup needs no lead).
    setOverride({ sel: new Set(base.sel), lead: base.lead === cid ? null : cid });
  }

  function save() {
    set.mutate(
      { assignments: [...view.sel].map((cid) => ({ crew_member_id: cid, is_lead: cid === view.lead })) },
      {
        onSuccess: (data) => {
          const warnings = data.warnings ?? [];
          const base =
            view.sel.size === 0 ? 'Crew cleared' : `Crew assigned (${data.assignments.length})`;
          if (warnings.length > 0) {
            toast.success(
              `${base} — heads up: ${warnings.map((w) => `${w.name} also on ${w.label}`).join('; ')}`,
            );
          } else {
            toast.success(base);
          }
          onClose();
        },
        onError: (err) => toast.error(`Assign failed: ${err.message}`),
      },
    );
  }

  const dayLabel = (d: string) =>
    new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <Dialog
      open
      onClose={onClose}
      busy={set.isPending}
      title={
        <>
          <Users className="w-4 h-4" />
          Assign crew to pickup
        </>
      }
    >
      <div className="p-4 space-y-3">
        <div>
          <div className="font-medium text-sm truncate">{pickup.title}</div>
          <div className="text-[11px] text-ink-faint">
            {view.sel.size} selected ·{' '}
            {pickup.scheduled_start
              ? `scheduled ${dayLabel(loadDay)} · load shown for that day`
              : 'no date yet — crew stay assigned and get the run once it’s scheduled'}
          </div>
        </div>

        {dispatch.isLoading || current.isLoading ? (
          <div className="text-xs text-ink-faint py-4 text-center">Loading crew…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-ink-faint py-4 text-center">No active crew.</div>
        ) : (
          <ul className="space-y-1 max-h-72 overflow-auto">
            {rows.map((row) => {
              const cm = row.crew_member;
              const selected = view.sel.has(cm.id);
              const isLead = view.lead === cm.id;
              const pct = row.capacity_hours > 0 ? Math.round((row.assigned_hours / row.capacity_hours) * 100) : 0;
              return (
                <li
                  key={cm.id}
                  className={cn(
                    'flex items-center gap-2 rounded-md border px-2 py-1.5',
                    selected ? 'border-accent bg-accent/5' : 'border-line',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggle(cm.id)}
                    aria-label={`Assign ${cm.user.display_name}`}
                    className="shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">
                      {cm.user.display_name}
                      <span className="text-ink-faint"> · {cm.crew_role}</span>
                    </div>
                    <div className="text-[11px] flex items-center gap-1.5">
                      <span
                        className={cn(
                          pct > 100 ? 'text-status-blocked-fg' : pct >= 80 ? 'text-amber-700' : 'text-ink-faint',
                        )}
                      >
                        {row.assigned_hours}h / {row.capacity_hours}h ({pct}%)
                      </span>
                      {row.has_conflict && (
                        <span className="inline-flex items-center gap-0.5 text-amber-700">
                          <TriangleAlert className="w-3 h-3" /> booked elsewhere
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={!selected}
                    onClick={() => makeLead(cm.id)}
                    title={isLead ? 'Lead' : 'Make lead (optional)'}
                    className={cn(
                      'shrink-0 inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded',
                      isLead ? 'text-amber-700 font-medium' : 'text-ink-faint hover:text-ink disabled:opacity-30',
                    )}
                  >
                    <Star className={cn('w-3.5 h-3.5', isLead && 'fill-amber-400')} />
                    Lead
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <p className="text-[11px] text-ink-faint">
          A time overlap with an install or another pickup is allowed — you’ll just get a heads-up.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose} disabled={set.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={set.isPending}>
            {set.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
